import React, { useEffect, useMemo } from "react";
import { getImageUrl } from "../../../api/client";
import insta1 from "../../../Assets/Instagram/insta1.jpg";
import insta2 from "../../../Assets/Instagram/insta2.jpg";
import insta3 from "../../../Assets/Instagram/insta3.jpg";
import insta4 from "../../../Assets/Instagram/insta4.jpg";
import insta5 from "../../../Assets/Instagram/insta5.jpg";
import InstagramOfficialEmbed from "./InstagramOfficialEmbed";
import InstagramNocodeApiEmbed from "./InstagramNocodeApiEmbed";
import "./Instagram.css";

/** Official IG profile for CTAs — https://www.instagram.com/gemfashionstudio */
const DEFAULT_INSTAGRAM_URL = "https://www.instagram.com/gemfashionstudio/";
const DEFAULT_SECTION_TITLE = "@gemfashionstudio";

const ELFSIGHT_PLATFORM = "https://elfsightcdn.com/platform.js";
const ELFSIGHT_APP_CLASS = "elfsight-app-4142097f-3ef6-4e97-9ee0-d5620ef17ebd";

const STATIC_FALLBACK_IMAGES = [insta1, insta2, insta3, insta4, insta5];

function isEnvTruthy(v) {
  if (v == null || typeof v !== "string") return false;
  const t = v.trim().toLowerCase();
  return t === "1" || t === "true" || t === "yes";
}

function resolveInstagramHref(title, envUrl) {
  const trimmed = typeof envUrl === "string" ? envUrl.trim() : "";
  if (trimmed) return trimmed.replace(/\/?$/, "/");
  const t = String(title || "").trim();
  if (t.startsWith("@")) {
    const handle = t.slice(1).replace(/[/\s]+$/u, "").replace(/^@+/u, "");
    if (handle) return `https://www.instagram.com/${handle}/`;
  }
  return DEFAULT_INSTAGRAM_URL;
}

/** Permalink Meta’s embed expects (profile + ig_embed utm). */
function toInstagramEmbedPermalink(profileUrl) {
  const trimmed = String(profileUrl || "").trim();
  if (!trimmed) return "";
  try {
    const u = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
    if (/utm_source=ig_embed/i.test(u.search)) return u.href;
    u.searchParams.set("utm_source", "ig_embed");
    u.searchParams.set("utm_campaign", "loading");
    return u.href;
  } catch (_) {
    const base = trimmed.replace(/\/?(\?.*)?$/, "").replace(/\/+$/, "");
    return `${base}/?utm_source=ig_embed&utm_campaign=loading`;
  }
}

function normalizeGridItems(images) {
  if (!Array.isArray(images)) return [];
  return images
    .map((item, i) => {
      const raw = typeof item === "string" ? item : item?.url;
      if (raw == null || typeof raw !== "string" || !raw.trim()) return null;
      const resolved = getImageUrl(raw.trim()) || raw.trim();
      const alt =
        typeof item === "object" && item !== null && typeof item.alt === "string" && item.alt.trim()
          ? item.alt.trim()
          : `Instagram style ${i + 1}`;
      const link =
        typeof item === "object" && item !== null && typeof item.link === "string" && item.link.trim()
          ? item.link.trim()
          : "";
      return { url: resolved, alt, link };
    })
    .filter(Boolean);
}

const LINK_ONLY = isEnvTruthy(process.env.REACT_APP_INSTAGRAM_LINK_ONLY_FALLBACK);
/** NoCodeAPI `/instagram/...` URL from dashboard — triggers @nocodeapi/embed-instagram-feed */
const NOCODE_INSTAGRAM_FEED_URL = (process.env.REACT_APP_NOCODEAPI_INSTAGRAM_FEED_URL || "").trim();
/** If ig.com is blocked — show image grid instead of blockquote/embeds.js */
const OFFICIAL_EMBED_DISABLED = isEnvTruthy(process.env.REACT_APP_INSTAGRAM_OFFICIAL_EMBED_DISABLED);
const ELFSIGHT_ENABLED = isEnvTruthy(process.env.REACT_APP_ELFSIGHT_INSTAGRAM_ENABLED);

function InstagramNativeGrid({ items, instagramHref }) {
  return (
    <div className="instagram__native">
      <div className="instagram__nativeGrid" role="list">
        {items.map((cell, i) => {
          const href = cell.link || instagramHref;
          return (
            <a
              key={`${cell.url}-${i}`}
              className="instagram__nativeCell"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              role="listitem"
            >
              <img src={cell.url} alt={cell.alt} loading="lazy" decoding="async" className="instagram__nativeImg" />
            </a>
          );
        })}
      </div>
      <p className="instagram__nativeFooter">
        <a className="instagram__fallback-link" href={instagramHref} target="_blank" rel="noopener noreferrer">
          View on Instagram
        </a>
      </p>
    </div>
  );
}

const Instagram = (props) => {
  const p = props?.props || {};
  const title = p.title || DEFAULT_SECTION_TITLE;
  const instagramHref = resolveInstagramHref(title, process.env.REACT_APP_INSTAGRAM_PROFILE_URL);
  const embedPermalink = useMemo(() => toInstagramEmbedPermalink(instagramHref), [instagramHref]);

  const cmsItems = useMemo(() => normalizeGridItems(p.images), [p.images]);
  const staticTileItems = useMemo(
    () =>
      STATIC_FALLBACK_IMAGES.map((url, i) => ({
        url,
        alt: `Gem Fashion Studios style ${i + 1}`,
        link: "",
      })),
    []
  );

  useEffect(() => {
    if (!ELFSIGHT_ENABLED) return;
    const id = "elfsight-platform-js";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.src = ELFSIGHT_PLATFORM;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  if (cmsItems.length > 0) {
    return (
      <div className="instagram instagram--native">
        <h2>{title}</h2>
        <InstagramNativeGrid items={cmsItems} instagramHref={instagramHref} />
      </div>
    );
  }

  if (LINK_ONLY) {
    return (
      <div className="instagram instagram--fallback">
        <h2>{title}</h2>
        <div className="instagram__fallback">
          <a
            className="instagram__fallback-link"
            href={instagramHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Instagram
          </a>
        </div>
      </div>
    );
  }

  if (ELFSIGHT_ENABLED) {
    return (
      <div className="instagram">
        <h2>{title}</h2>
        <div className="instagram__embed">
          <div className={ELFSIGHT_APP_CLASS} data-elfsight-app-lazy="" />
        </div>
      </div>
    );
  }

  if (NOCODE_INSTAGRAM_FEED_URL) {
    return (
      <div className="instagram instagram--nocode">
        <h2>{title}</h2>
        <InstagramNocodeApiEmbed feedUrl={NOCODE_INSTAGRAM_FEED_URL} />
        <p className="instagram__officialCaption">
          <a className="instagram__fallback-link" href={instagramHref} target="_blank" rel="noopener noreferrer">
            Open profile on Instagram
          </a>
        </p>
      </div>
    );
  }

  if (OFFICIAL_EMBED_DISABLED) {
    return (
      <div className="instagram instagram--native">
        <h2>{title}</h2>
        <InstagramNativeGrid items={staticTileItems} instagramHref={instagramHref} />
      </div>
    );
  }

  return (
    <div className="instagram instagram--official">
      <h2>{title}</h2>
      <div className="instagram__officialWrap">
        <InstagramOfficialEmbed permalink={embedPermalink} />
      </div>
      <p className="instagram__officialCaption">
        <a className="instagram__fallback-link" href={instagramHref} target="_blank" rel="noopener noreferrer">
          Open profile on Instagram
        </a>
      </p>
    </div>
  );
};

export default Instagram;
